import type { ReactNode } from 'react';
import type { GridColumn } from '@components/grid/useGridView';
import { SUGGESTED_META, fmtTs, type ThreadRow } from '@lib/care-model';
import { NO_TEXT_WORDING, messagePreview, previewLine, type PreviewSource } from '@lib/message-preview';
import { STOP_PENDING_HELP, STOP_PENDING_WORDS, waitingAge } from '@lib/stop-pending';
import { claimHolderName, type HolderFields } from '@lib/claim-holder';

/**
 * THE INBOUND QUEUE'S ROW PIECES, shared by both views (William, 16 Oct 2026). The grouped view (Group by
 * member, ThreadTable) was drawn "like the print" that afternoon: a tick box first, the row's number, the
 * name over the member id, the Contact cell, alternate rows banded. At 8:20 PM he asked: "Make sure the
 * 'flat events' screen looks the same as the group by member". So the pieces live here once, and the flat
 * view (QueueTable's rows look) draws its rows with the same ones rather than a copy.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */

/** The table class of the print-like rows (index.css: `.dt.dt-rows`). */
export const ROWS_TABLE_CLASS = 'dt dt-rows';

/** Alternate rows banded, like the print screen. */
export function rowBand(i: number): string {
  return i % 2 === 0 ? 'var(--row-band-a)' : 'var(--row-band-b)';
}

/**
 * The Claimed by column (William, 16 Oct 2026 8:52 PM: "when claimed goes in the main queue with the user
 * name (make sure we ahve a column for this in teh main queue)"): the holder's name, a dash when nobody
 * holds the row. Key 'handledBy' kept from the Handled by column it replaces, so a remembered width and
 * place carry over; shown whatever the Status filter (it was hidden on the New view before).
 */
export function claimedByColumn<T>(holderOf: (r: T) => HolderFields | undefined): GridColumn<T> {
  return {
    key: 'handledBy', label: 'Claimed by', tdClassName: 'small', tdStyle: { whiteSpace: 'nowrap' },
    sortValue: (r) => claimHolderName(holderOf(r) ?? {}),
    render: (r) => claimHolderName(holderOf(r) ?? {}) || <span className="muted">-</span>,
  };
}

/** The columns the message line leaves alone: it starts under the member, as his picture has it. */
export const LINE2_LEAD: readonly string[] = ['select', 'no', 'open'];

/**
 * The message line under a queue row (William, 16 Oct 2026 8:30 PM): what kind of message, then its words,
 * at most two lines and cut off (index.css `.queue-line2`); the whole line is its tooltip.
 */
export function MessageLine({ r }: { r: PreviewSource }) {
  const p = messagePreview(r);
  return (
    <div className="queue-line2" title={previewLine(p)} data-testid="queue-line2">
      <b>{p.lead}:</b> {p.text ? p.text : <span className="muted">{NO_TEXT_WORDING}</span>}
    </div>
  );
}

/** The tick box column: a header box that ticks every row shown (or clears them), one box per row. */
export function tickColumn<T>(o: {
  /** Absent: the column draws an empty heading and inert boxes (the table was given no selection). */
  on: boolean;
  allShown: boolean;
  onToggleAll: () => void;
  isChecked: (r: T) => boolean;
  onToggle: (r: T) => void;
  ariaLabel: (r: T) => string;
  testId: (r: T) => string;
  /** Why a row's box is disabled ('' = tickable); the reason is its tooltip. */
  blocked?: ((r: T) => string) | undefined;
}): GridColumn<T> {
  return {
    key: 'select',
    label: o.on ? (
      <input type="checkbox" aria-label="Tick every row shown" checked={o.allShown} onChange={o.onToggleAll} />
    ) : '',
    menuLabel: 'Tick box', locked: true, stopRowClick: true,
    thStyle: { width: 30 },
    tdStyle: { width: 30 },
    render: (r) => {
      const why = o.blocked?.(r) ?? '';
      return (
        <input
          type="checkbox"
          aria-label={o.ariaLabel(r)}
          checked={!why && o.isChecked(r)}
          disabled={!!why}
          title={why || undefined}
          onChange={() => o.onToggle(r)}
          onKeyDown={(e) => e.stopPropagation()}
          data-testid={o.testId(r)}
        />
      );
    },
  };
}

/** The print screen's NO. column: the row's place on screen, 1 at the top. */
export function rowNoColumn<T>(): GridColumn<T> {
  return { key: 'no', label: 'No.', tdClassName: 'cell-no', thStyle: { width: 44 }, render: (_r, i) => i + 1 };
}

/** A cell on two lines: the main line over a quieter one (the name over the member id). */
export function TwoLineCell({ main, sub, subTestId, extra }: { main: ReactNode; sub: ReactNode; subTestId?: string; extra?: ReactNode }) {
  return (
    <div className="cell-2">
      <span className="cell-main">
        {main}
        {extra}
      </span>
      <span className="cell-sub" data-testid={subTestId}>{sub}</span>
    </div>
  );
}

/** The Contact cell: the phone and the email, the phone first; either alone shows alone; none shows a dash. */
export function ContactCell({ phone, email }: { phone: ReactNode; email: string }) {
  if (!phone && !email) return <span className="muted">-</span>;
  if (phone && email) return <TwoLineCell main={phone} sub={email} />;
  return <span>{phone || email}</span>;
}

/** The Suggested action cell, with the RE-SUBSCRIBED pill beside it (a thread or a single entry). */
export function SuggestedCell({ r, stopWords }: {
  /** Which STOP the START came after, in the row's own words ("the STOP in this thread", "this STOP"). */
  stopWords: string;
  r: Pick<ThreadRow, 'suggestedAction' | 'suggestedActionReason' | 'suggestedActionConfidence'> & { resubscribedAfterStop?: boolean | undefined; resubscribedStartTs?: string | undefined; stopPending?: boolean | undefined; stopSince?: string | undefined };
}) {
  const sug = r.suggestedAction ? SUGGESTED_META[r.suggestedAction] : null;
  return (
    <>
      {/* PART N (William, 17 Oct 2026 1:05 AM): a stop waits for approval on this case; its texting block is on. */}
      {r.stopPending && (
        <span className="pill pill-danger" data-testid="stop-pending-pill" title={STOP_PENDING_HELP} style={{ marginRight: 4 }}>
          {STOP_PENDING_WORDS}{r.stopSince ? ` · ${waitingAge(r.stopSince)}` : ''}
        </span>
      )}
      {sug ? (
        <span
          className={`pill ${sug.pill}`.trim()}
          title={`${r.suggestedActionReason}${r.suggestedActionConfidence === 'low' ? ' (text rule: verify before acting)' : ''}`}
          // A low-confidence suggestion is a hint from a text rule, not a parsed keyword.
          // Dashed border so the operator can see, at a glance, which ones to trust.
          style={r.suggestedActionConfidence === 'low' ? { borderStyle: 'dashed' } : undefined}
        >
          {sug.label}
        </span>
      ) : (
        <span className="muted">-</span>
      )}
      {/* Sits beside the suggestion on purpose: on a reversed STOP the suggestion still
          reads "Opt out", and that chip is precisely what invites the operator to record
          the disposition the drawer then blocks. The contradiction has to be visible in
          the same glance, not one column away. */}
      {r.resubscribedAfterStop && (
        <span
          className="pill pill-success"
          title={`This number texted START on ${fmtTs(r.resubscribedStartTs ?? '')}, after ${stopWords}. The SMS platform has already re-subscribed the member; recording an opt-out here would mark them do-not-solicit permanently.`}
          style={{ marginLeft: 4 }}
        >
          RE-SUBSCRIBED
        </span>
      )}
    </>
  );
}
